import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { JwtService } from 'src/auth/services/jwt.service';
import { PageAlertDto, PageAlertsClient, PageAlertStyle } from 'src/shared/services/api.service';

@Component({
  selector: 'cert-page-alert-banner',
  templateUrl: './page-alert-banner.component.html',
  styles: `
    :host ::ng-deep [markdown] > :last-child {
      margin-bottom: 0;
    }
  `,
  standalone: false,
})
export class PageAlertBannerComponent implements OnInit {
  // Omit this input to derive the page from the host component's route. Components reused across
  // several routes must omit it so each route resolves to its own page registry path.
  @Input() page?: string;

  alerts = signal<PageAlertDto[]>([]);

  private readonly pageAlertsClient = inject(PageAlertsClient);
  private readonly jwtService = inject(JwtService);
  private readonly route = inject(ActivatedRoute);

  ngOnInit() {
    const roles = this.jwtService.roles;
    const page = this.page ?? this.resolvePageFromRoute();

    const requests =
      roles.length > 0
        ? roles.map((role) => this.pageAlertsClient.getApplicablePageAlerts(page, role))
        : [this.pageAlertsClient.getApplicablePageAlerts(page, '')];

    forkJoin(requests).subscribe((results) => {
      const merged = new Map<string, PageAlertDto>();
      results.flat().forEach((alert) => {
        const key = `${alert.style}|${alert.description}`;

        if (!merged.has(key)) {
          merged.set(key, alert);
        }
      });
      this.alerts.set(Array.from(merged.values()));
    });
  }

  getAlertClass(alert: PageAlertDto): string {
    switch (alert.style) {
      case PageAlertStyle.Success:
        return 'alert-success';
      case PageAlertStyle.Warning:
        return 'alert-warning';
      case PageAlertStyle.Error:
        return 'alert-danger';
      case PageAlertStyle.Info:
      default:
        return 'alert-info';
    }
  }

  // Rebuilds the same path the page registry generator produces by joining the route config
  // segments from the root down to this banner's host route, keeping ':param' placeholders.
  private resolvePageFromRoute(): string {
    return this.route.pathFromRoot
      .map((route) => route.routeConfig?.path)
      .filter((segment): segment is string => !!segment)
      .join('/');
  }
}
